'use client'

import { useCallback, useEffect, useRef, useState } from 'react'

import { QUESTIONNAIRE_SECTIONS } from '@/lib/client-intake/questionnaire'

// The client onboarding questionnaire + data room. Autosaves (debounced)
// while the client types so "pause and come back with the same link" is
// literally true; Submit finalizes. File uploads go straight to the data
// room and stay available even after submission.

const AUTOSAVE_MS = 1500

const inputClass =
  'mt-2 w-full rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3 text-sm text-white placeholder-neutral-600 focus:border-kingdom-gold/60 focus:outline-none focus:ring-1 focus:ring-kingdom-gold/40'

function PrefillMarker() {
  return (
    <p className="mt-1 text-xs italic text-kingdom-gold/70">
      from your application — edit if this should be different
    </p>
  )
}

function FieldControl({ field, value, onChange, disabled }) {
  if (field.type === 'textarea') {
    return (
      <textarea
        value={value || ''}
        onChange={(e) => onChange(field.key, e.target.value)}
        disabled={disabled}
        rows={4}
        className={inputClass + ' resize-y disabled:opacity-50'}
      />
    )
  }
  if (field.type === 'radio') {
    return (
      <div className="mt-2 grid gap-2">
        {(field.options || []).map((option) => (
          <label
            key={option}
            className={`flex cursor-pointer items-start gap-3 rounded-xl border px-4 py-3 text-sm transition ${
              value === option
                ? 'border-kingdom-gold/60 bg-kingdom-gold/[0.08] text-white'
                : 'border-white/10 bg-white/[0.03] text-neutral-300 hover:border-white/25'
            } ${disabled ? 'pointer-events-none opacity-50' : ''}`}
          >
            <input
              type="radio"
              name={field.key}
              checked={value === option}
              onChange={() => onChange(field.key, option)}
              disabled={disabled}
              className="mt-0.5 accent-[#C9A84C]"
            />
            <span>{option}</span>
          </label>
        ))}
      </div>
    )
  }
  if (field.type === 'checkboxes') {
    const selected = Array.isArray(value) ? value : []
    return (
      <div className="mt-2 grid gap-2">
        {(field.options || []).map((option) => {
          const checked = selected.includes(option)
          return (
            <label
              key={option}
              className={`flex cursor-pointer items-start gap-3 rounded-xl border px-4 py-3 text-sm transition ${
                checked
                  ? 'border-kingdom-gold/60 bg-kingdom-gold/[0.08] text-white'
                  : 'border-white/10 bg-white/[0.03] text-neutral-300 hover:border-white/25'
              } ${disabled ? 'pointer-events-none opacity-50' : ''}`}
            >
              <input
                type="checkbox"
                checked={checked}
                onChange={() =>
                  onChange(
                    field.key,
                    checked ? selected.filter((v) => v !== option) : [...selected, option],
                  )
                }
                disabled={disabled}
                className="mt-0.5 accent-[#C9A84C]"
              />
              <span>{option}</span>
            </label>
          )
        })}
      </div>
    )
  }
  return (
    <input
      type={field.type === 'email' ? 'email' : field.type === 'tel' ? 'tel' : 'text'}
      value={value || ''}
      onChange={(e) => onChange(field.key, e.target.value)}
      disabled={disabled}
      className={inputClass + ' disabled:opacity-50'}
    />
  )
}

export function ClientIntakeForm({ token, savedAnswers, prefill, sources, initialFiles, submitted }) {
  const saved = savedAnswers || {}
  const pre = prefill || {}
  const src = sources || {}

  // Display value per field: savedAnswers[key] ?? prefill[key] ?? ''
  // (spec §Render-time merge) — saved wins because a key present in
  // `saved` is, by definition, already touched.
  const [values, setValues] = useState(() => ({ ...pre, ...saved }))
  // Core semantics: a key present in `saved` (even '') is touched/frozen.
  // Untouched fields (absent here) stay eligible for prefill display and
  // are never sent on save — only the client's own edits, plus whatever was
  // already touched in prior sessions, ride the save payload.
  const [touched, setTouched] = useState(() => new Set(Object.keys(saved)))
  // Evaluated once from the initial props — the banner is a one-time,
  // load-time signal ("we prefilled something this load"), not a live
  // indicator that tracks in-session edits.
  const [prefilledCount] = useState(
    () => Object.keys(pre).filter((key) => !(key in saved)).length,
  )
  const [bannerDismissed, setBannerDismissed] = useState(false)

  const [isSubmitted, setIsSubmitted] = useState(!!submitted)
  const [saveState, setSaveState] = useState('idle') // idle | saving | saved | error
  const [submitBusy, setSubmitBusy] = useState(false)
  const [submitError, setSubmitError] = useState(null)

  const [files, setFiles] = useState(initialFiles || [])
  const [uploadBusy, setUploadBusy] = useState(false)
  const [uploadError, setUploadError] = useState(null)
  const fileInputRef = useRef(null)

  const saveTimer = useRef(null)
  const valuesRef = useRef(values)
  valuesRef.current = values
  const touchedRef = useRef(touched)
  touchedRef.current = touched

  const save = useCallback(
    async (submit = false) => {
      setSaveState('saving')
      try {
        // Dirty tracking: only touched fields ride the save payload — an
        // untouched-but-prefilled value is never sent, so it stays fresh
        // against future application changes (spec §Save vs Submit).
        const touchedAnswers = {}
        for (const key of touchedRef.current) {
          touchedAnswers[key] = valuesRef.current[key] ?? ''
        }
        const res = await fetch('/api/client-intake/save', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token, answers: touchedAnswers, submit }),
        })
        const data = await res.json().catch(() => null)
        if (!res.ok || !data?.ok) {
          if (data?.submitted) {
            setIsSubmitted(true)
            return true
          }
          throw new Error(data?.error || `HTTP ${res.status}`)
        }
        setSaveState('saved')
        return true
      } catch (err) {
        setSaveState('error')
        if (submit) setSubmitError(err.message)
        return false
      }
    },
    [token],
  )

  const onChange = useCallback(
    (key, value) => {
      setValues((prev) => ({ ...prev, [key]: value }))
      setTouched((prev) => (prev.has(key) ? prev : new Set(prev).add(key)))
      setSaveState('idle')
      if (saveTimer.current) clearTimeout(saveTimer.current)
      saveTimer.current = setTimeout(() => save(false), AUTOSAVE_MS)
    },
    [save],
  )

  useEffect(() => () => saveTimer.current && clearTimeout(saveTimer.current), [])

  async function onSubmit() {
    if (saveTimer.current) clearTimeout(saveTimer.current)
    setSubmitBusy(true)
    setSubmitError(null)
    const ok = await save(true)
    setSubmitBusy(false)
    if (ok) setIsSubmitted(true)
  }

  async function onFilesPicked(fileList) {
    setUploadError(null)
    setUploadBusy(true)
    try {
      for (const file of Array.from(fileList)) {
        const form = new FormData()
        form.append('token', token)
        form.append('file', file)
        const res = await fetch('/api/client-intake/upload', { method: 'POST', body: form })
        const data = await res.json().catch(() => null)
        if (!res.ok || !data?.ok) {
          setUploadError(data?.error || `Upload failed (HTTP ${res.status})`)
          continue
        }
        setFiles((prev) => [data.file, ...prev])
      }
    } finally {
      setUploadBusy(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  const answeredSections = QUESTIONNAIRE_SECTIONS.filter((s) =>
    s.fields.some((f) => {
      const v = values[f.key]
      return Array.isArray(v) ? v.length > 0 : !!v
    }),
  ).length

  return (
    <div className="mt-10">
      {isSubmitted && (
        <div className="mb-8 rounded-2xl border border-kingdom-gold/30 bg-kingdom-gold/[0.06] px-6 py-5 text-center">
          <p className="font-display text-lg font-medium">Thanks — that&apos;s everything.</p>
          <p className="mt-1 text-sm text-neutral-300">
            Your answers are with the team. You can still add files below any time.
          </p>
        </div>
      )}

      {!isSubmitted && prefilledCount > 0 && !bannerDismissed && (
        <div className="mb-8 flex items-start justify-between gap-4 rounded-2xl border border-kingdom-gold/30 bg-kingdom-gold/[0.06] px-6 py-4">
          <p className="text-sm text-neutral-200">
            We&apos;ve pre-filled some answers from your application — please review and edit
            anything that&apos;s changed.
          </p>
          <button
            type="button"
            onClick={() => setBannerDismissed(true)}
            aria-label="Dismiss"
            className="shrink-0 text-neutral-500 hover:text-neutral-200"
          >
            ×
          </button>
        </div>
      )}

      {QUESTIONNAIRE_SECTIONS.map((section, i) => (
        <section
          key={section.key}
          className="border-b border-white/10 py-8 first:border-t"
        >
          <div className="flex items-baseline gap-4">
            <span className="font-mono text-xs font-semibold tracking-widest text-kingdom-gold">
              {String(i + 1).padStart(2, '0')}
            </span>
            <h2 className="font-display text-xl font-medium tracking-tight">{section.title}</h2>
          </div>
          <p className="mt-1 pl-9 text-sm text-neutral-500">{section.why}</p>
          <div className="mt-5 grid gap-5 pl-0 sm:pl-9">
            {section.fields.map((field) => (
              <div key={field.key}>
                <label className="block text-sm font-medium text-neutral-200">
                  {field.label}
                  {field.optional && (
                    <span className="ml-2 rounded-full bg-kingdom-gold/10 px-2 py-0.5 text-[0.6rem] font-semibold tracking-wider text-kingdom-gold uppercase">
                      optional
                    </span>
                  )}
                </label>
                {field.hint && <p className="mt-0.5 text-xs text-neutral-500">{field.hint}</p>}
                {field.example && (
                  <p className="mt-1 rounded-lg bg-kingdom-gold/[0.06] px-3 py-2 text-xs text-neutral-400">
                    {field.example}
                  </p>
                )}
                {src[field.key] && !touched.has(field.key) && <PrefillMarker />}
                <FieldControl
                  field={field}
                  value={values[field.key]}
                  onChange={onChange}
                  disabled={isSubmitted}
                />
              </div>
            ))}
          </div>
        </section>
      ))}

      {/* Data room */}
      <section className="border-b border-white/10 py-8">
        <div className="flex items-baseline gap-4">
          <span className="font-mono text-xs font-semibold tracking-widest text-kingdom-gold">
            ↑
          </span>
          <h2 className="font-display text-xl font-medium tracking-tight">Your files</h2>
        </div>
        <p className="mt-1 pl-9 text-sm text-neutral-500">
          Logo, photos, price lists, brand docs, spreadsheets — anything useful for the build.
          PDFs, images, Word/Excel, CSV, up to 10MB each.
        </p>
        <div className="mt-5 sm:pl-9">
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".pdf,.png,.jpg,.jpeg,.webp,.svg,.doc,.docx,.xls,.xlsx,.csv,.txt"
            onChange={(e) => e.target.files?.length && onFilesPicked(e.target.files)}
            className="hidden"
            id="dataroom-input"
          />
          <label
            htmlFor="dataroom-input"
            className={`flex cursor-pointer items-center justify-center rounded-2xl border border-dashed border-white/20 bg-white/[0.02] px-6 py-8 text-sm text-neutral-400 transition hover:border-kingdom-gold/50 hover:text-neutral-200 ${
              uploadBusy ? 'pointer-events-none opacity-60' : ''
            }`}
          >
            {uploadBusy ? 'Uploading…' : 'Click to choose files (or drop them on this button)'}
          </label>
          {uploadError && <p className="mt-2 text-sm text-red-400">{uploadError}</p>}
          {files.length > 0 && (
            <ul className="mt-4 grid gap-2">
              {files.map((f) => (
                <li
                  key={f.id}
                  className="flex items-center justify-between rounded-xl border border-white/10 bg-white/[0.03] px-4 py-2.5 text-sm"
                >
                  <span className="truncate text-neutral-200">{f.filename}</span>
                  <span className="ml-4 shrink-0 text-xs text-kingdom-gold">received ✓</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      {/* Sticky action bar */}
      {!isSubmitted && (
        <div className="sticky bottom-4 mt-8 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-white/10 bg-neutral-900/95 px-5 py-4 backdrop-blur">
          <p className="text-xs text-neutral-400">
            <span className="font-semibold text-white">
              {answeredSections} of {QUESTIONNAIRE_SECTIONS.length}
            </span>{' '}
            sections started ·{' '}
            {saveState === 'saving'
              ? 'Saving…'
              : saveState === 'saved'
                ? 'Saved ✓'
                : saveState === 'error'
                  ? 'Save failed — check your connection'
                  : 'Autosaves as you type'}
          </p>
          <button
            type="button"
            onClick={onSubmit}
            disabled={submitBusy}
            className="rounded-full bg-kingdom-gold px-6 py-2.5 text-sm font-semibold text-neutral-950 transition hover:bg-[#d9b95e] disabled:opacity-60"
          >
            {submitBusy ? 'Sending…' : "I'm done — send my answers"}
          </button>
          {submitError && <p className="w-full text-sm text-red-400">{submitError}</p>}
        </div>
      )}

      <p className="mt-10 text-center text-xs text-neutral-600">
        Questions?{' '}
        <a href="mailto:info@kingdomkode.com" className="text-neutral-400 underline">
          info@kingdomkode.com
        </a>
      </p>
    </div>
  )
}
